import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAssistantVoiceToken } from "api";
import { hasAssistantReviewWidgets } from "../assistantReviewMessage";
import useDeepgramFluxStt from "./useDeepgramFluxStt";
// TTS provider: ElevenLabs via backend proxy. The Deepgram Aura player
// (useAuraTtsPlayer, browser-direct websocket) has the same interface —
// swapping providers is this one import.
import useElevenLabsTtsPlayer from "./useElevenLabsTtsPlayer";

// The inline dock leaves the app usable while a session runs, so the user may
// deliberately park the assistant and go read something. 90s was tuned for a
// full-screen overlay nobody could look away from. Still finite: a live session
// holds an open Deepgram socket and a hot microphone.
const SILENCE_AUTO_CLOSE_MS = 5 * 60 * 1000;
const TIMEOUT_APOLOGY =
  "Sorry, that took too long. Try asking a narrower question.";
// The assistant speaks first when a session opens; like status filler, the
// greeting never flips the state to "speaking" (answerStartedRef stays false).
const SESSION_GREETINGS = [
  "Hey! What can I help you with?",
  "Hi, I'm listening. What do you need?",
  "Hey there. What's on your plate today?",
  "Hi! Ask me anything about your meetings, calendar, or tasks.",
];

const MIC_DENIED_MESSAGE =
  "Microphone access is blocked. Allow it in your browser settings to use voice mode.";
const CONNECTION_ERROR_MESSAGE =
  "Voice mode could not connect. Check your connection and try again.";
const PLAYBACK_ERROR_MESSAGE =
  "Voice playback failed. The response is still available in chat.";

// The hands-free session state machine:
// idle -> connecting -> listening -> thinking -> speaking -> listening -> ...
// Barge-in (user speaks while thinking/speaking) cancels the in-flight turn
// and returns to listening. Turns run through the shared useAssistantChat
// sendMessage, so the conversation lands in the normal chat transcript.
// NOTE: every callback below depends only on stable functions destructured
// from the audio hooks — startSession/stopSession identity must not change
// across renders, or the overlay's mount effect would restart the session.
const useVoiceConversation = ({ sendMessage, onAutoClose }) => {
  const [voiceState, setVoiceState] = useState("idle");
  // Two speakers, two fields. The dock shows one utterance at a time, so
  // whichever side takes the floor clears the other — a single shared caption
  // could not express that, and it lost the question the moment an answer
  // started streaming.
  const [userTranscript, setUserTranscript] = useState("");
  const [assistantText, setAssistantText] = useState("");
  const [statusLabel, setStatusLabel] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [reviewMessage, setReviewMessage] = useState(null);
  const [isMicMuted, setIsMicMuted] = useState(false);

  const sessionActiveRef = useRef(false);
  const voiceStateRef = useRef("idle");
  const abortControllerRef = useRef(null);
  const answerStartedRef = useRef(false);
  const turnRequestActiveRef = useRef(false);
  const playbackFailedRef = useRef(false);
  const silenceTimerRef = useRef(null);

  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;
  const onAutoCloseRef = useRef(onAutoClose);
  onAutoCloseRef.current = onAutoClose;

  const setState = useCallback((nextState) => {
    voiceStateRef.current = nextState;
    setVoiceState(nextState);
  }, []);

  const getAccessToken = useCallback(async () => {
    const { accessToken } = await fetchAssistantVoiceToken();
    return accessToken;
  }, []);

  const clearSilenceTimer = useCallback(() => {
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
  }, []);

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      if (sessionActiveRef.current && voiceStateRef.current === "listening") {
        onAutoCloseRef.current?.();
      }
    }, SILENCE_AUTO_CLOSE_MS);
  }, [clearSilenceTimer]);

  const {
    speak: ttsSpeak,
    flush: ttsFlush,
    stopAndClear: ttsStopAndClear,
    disconnect: ttsDisconnect,
    isSpeaking: isTtsSpeaking,
    getOutputAnalyser: getTtsAnalyser,
  } = useElevenLabsTtsPlayer({
    onPlaybackStart: () => {
      if (!sessionActiveRef.current) return;
      playbackFailedRef.current = false;
      setErrorMessage("");
      // Greeting/ack filler plays during "listening"/"thinking"; only answer
      // audio flips the state to "speaking".
      if (answerStartedRef.current) {
        setState("speaking");
      }
    },
    onPlaybackEnd: () => {
      if (!sessionActiveRef.current) return;
      if (voiceStateRef.current === "speaking") {
        setState("listening");
        armSilenceTimer();
      }
    },
    onError: () => {
      if (!sessionActiveRef.current) return;
      playbackFailedRef.current = true;
      setErrorMessage(PLAYBACK_ERROR_MESSAGE);
      // Playback loss is non-fatal: keep the text response available and
      // return control to the microphone after the text request has settled.
      if (
        voiceStateRef.current === "thinking" &&
        !turnRequestActiveRef.current
      ) {
        setState("listening");
        armSilenceTimer();
      }
    },
  });

  const handleAnswerDelta = useCallback(
    (text) => {
      if (!sessionActiveRef.current) return;
      if (!answerStartedRef.current) {
        answerStartedRef.current = true;
        // Cut any ack still playing mid-word; the answer takes the floor.
        ttsStopAndClear();
        setAssistantText("");
        // Only one speaker holds the floor in the dock. Drop the echo of the
        // question so the answer is the whole of what is on screen.
        setUserTranscript("");
      }
      setAssistantText((current) => current + text);
      ttsSpeak(text);
    },
    [ttsStopAndClear, ttsSpeak],
  );

  const runTurn = useCallback(
    async (transcript) => {
      setState("thinking");
      setStatusLabel("");
      setUserTranscript(transcript);
      // Drop the previous answer as soon as a new question lands, or the dock
      // would show the last reply underneath the new question.
      setAssistantText("");
      clearSilenceTimer();
      answerStartedRef.current = false;
      playbackFailedRef.current = false;
      turnRequestActiveRef.current = true;

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const assistantMessage = await sendMessageRef.current(transcript, {
        voiceMode: true,
        signal: abortController.signal,
        // Progress labels are visual-only (the overlay status line); the ack
        // is the only spoken filler — step statuses read aloud felt noisy.
        onProgress: (progress) => {
          if (abortController.signal.aborted || !sessionActiveRef.current) return;
          setStatusLabel(progress.label || "");
        },
        // Instant acknowledgement ("Sure, drafting that email now") — plays
        // like status filler while the agent works; the first answer delta
        // cuts it via handleAnswerDelta's stopAndClear.
        onAck: (text) => {
          if (abortController.signal.aborted || !sessionActiveRef.current) return;
          if (answerStartedRef.current) return;
          setStatusLabel(text);
          ttsSpeak(text);
          ttsFlush();
        },
        onDelta: (text) => {
          if (abortController.signal.aborted) return;
          handleAnswerDelta(text);
        },
      });

      turnRequestActiveRef.current = false;

      if (!sessionActiveRef.current || abortController.signal.aborted) return;
      abortControllerRef.current = null;
      setStatusLabel("");

      if (!assistantMessage) {
        // Errors (including the backend timeout frame) already landed in the
        // chat transcript; say a short apology and keep listening.
        answerStartedRef.current = true;
        ttsStopAndClear();
        ttsSpeak(TIMEOUT_APOLOGY);
        ttsFlush();
        return;
      }

      if (hasAssistantReviewWidgets(assistantMessage)) {
        setReviewMessage(assistantMessage);
      }
      if (!answerStartedRef.current) {
        // Voice turns always deliver at least one delta; this is a safety
        // net so a reply is never silently dropped.
        handleAnswerDelta(assistantMessage.content);
      }
      ttsFlush();
      if (
        playbackFailedRef.current &&
        voiceStateRef.current === "thinking"
      ) {
        setState("listening");
        armSilenceTimer();
      }
    },
    [
      setState,
      clearSilenceTimer,
      handleAnswerDelta,
      ttsStopAndClear,
      ttsSpeak,
      ttsFlush,
      armSilenceTimer,
    ],
  );

  const cancelInFlightTurn = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    ttsStopAndClear();
  }, [ttsStopAndClear]);

  const {
    connect: sttConnect,
    disconnect: sttDisconnect,
    setMuted: sttSetMuted,
    getMicStream,
  } = useDeepgramFluxStt({
    getAccessToken,
    onInterimTranscript: (transcript) => {
      if (!sessionActiveRef.current) return;
      if (voiceStateRef.current === "listening") {
        setUserTranscript(transcript);
        armSilenceTimer();
      }
    },
    onSpeechStarted: () => {
      if (!sessionActiveRef.current) return;
      const state = voiceStateRef.current;
      // Barge-in: the user talking over the assistant cancels the turn.
      if (state === "speaking" || state === "thinking") {
        cancelInFlightTurn();
        setUserTranscript("");
        setAssistantText("");
        setState("listening");
        armSilenceTimer();
        return;
      }
      // In listening state only filler audio (greeting) can be playing;
      // cut it as soon as the user starts talking.
      if (state === "listening") {
        ttsStopAndClear();
        // The user takes the floor: clear the last answer so the dock shows
        // their words alone rather than stacking a second speaker under them.
        setAssistantText("");
      }
    },
    onEndOfTurn: (transcript) => {
      if (!sessionActiveRef.current) return;
      if (voiceStateRef.current !== "listening" || !transcript) return;
      runTurn(transcript);
    },
    onError: () => {
      if (!sessionActiveRef.current) return;
      setErrorMessage(CONNECTION_ERROR_MESSAGE);
      setState("error");
    },
  });

  const startSession = useCallback(async () => {
    if (sessionActiveRef.current) return;
    sessionActiveRef.current = true;
    setErrorMessage("");
    setUserTranscript("");
    setAssistantText("");
    setStatusLabel("");
    setReviewMessage(null);
    setIsMicMuted(false);
    setState("connecting");

    try {
      await sttConnect();
      if (!sessionActiveRef.current) return;
      setState("listening");
      armSilenceTimer();

      // The assistant opens the conversation instead of waiting silently.
      const greeting =
        SESSION_GREETINGS[Math.floor(Math.random() * SESSION_GREETINGS.length)];
      // The greeting is the assistant talking, not something the user said.
      setAssistantText(greeting);
      ttsSpeak(greeting);
      ttsFlush();
    } catch (error) {
      sessionActiveRef.current = false;
      setErrorMessage(
        error?.name === "NotAllowedError" ? MIC_DENIED_MESSAGE : CONNECTION_ERROR_MESSAGE,
      );
      setState("error");
    }
  }, [setState, sttConnect, armSilenceTimer, ttsSpeak, ttsFlush]);

  const stopSession = useCallback(() => {
    sessionActiveRef.current = false;
    turnRequestActiveRef.current = false;
    playbackFailedRef.current = false;
    clearSilenceTimer();
    cancelInFlightTurn();
    sttDisconnect();
    ttsDisconnect();
    setState("idle");
    setUserTranscript("");
    setAssistantText("");
    setStatusLabel("");
    setReviewMessage(null);
  }, [clearSilenceTimer, cancelInFlightTurn, sttDisconnect, ttsDisconnect, setState]);

  const toggleMicMuted = useCallback(() => {
    setIsMicMuted((current) => {
      sttSetMuted(!current);
      return !current;
    });
  }, [sttSetMuted]);

  const clearReviewMessage = useCallback(() => {
    setReviewMessage(null);
  }, []);

  useEffect(() => () => stopSession(), [stopSession]);

  return {
    voiceState,
    userTranscript,
    assistantText,
    statusLabel,
    errorMessage,
    reviewMessage,
    clearReviewMessage,
    isMicMuted,
    isTtsSpeaking,
    startSession,
    stopSession,
    toggleMicMuted,
    getMicStream,
    getTtsAnalyser,
  };
};

export default useVoiceConversation;
